import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  QaiTableComponent,
  TableRow,
  QaiLoadingComponent,
  HeaderConfig,
  SortDirection,
  QaiFieldComponent,
  QaiInputComponent,
  QaiIconComponent,
  QaiSlotDirective,
} from '@qai/ui';
import { ActivatedRoute, Router } from '@angular/router';
import { IInputUser } from 'src/app/interfaces/self-serve/assignment-manager/responses/iusers-with-teams';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { localeLowerCase } from '../../../../../../utils/locale-case.util';

@Component({
  selector: 'app-users-list',
  standalone: true,
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
  imports: [
    QaiTableComponent,
    QaiLoadingComponent,
    QaiFieldComponent,
    QaiInputComponent,
    QaiIconComponent,
    QaiSlotDirective,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class UsersListComponent implements OnChanges {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translocoService = inject(TranslocoService);

  @Input() users: IInputUser[] = [];
  @Input() isLoading: boolean = false;

  // ── i18n keys ─────────────────────────────────────────────────────────────
  readonly i18nKeys = {
    searchPlaceholder: 'simulationBuilder.SEARCH_USERS_PLACEHOLDER',
    noUsersFound:      'simulationBuilder.NO_USERS_FOUND',
    colName:           'simulationBuilder.NAME_LABEL',
    colEmail:          'simulationBuilder.COL_EMAIL',
    colViewUser:       'simulationBuilder.COL_VIEW_USER',
    previousLabel:     'simulationBuilder.PAGINATION_PREVIOUS',
    nextLabel:         'simulationBuilder.PAGINATION_NEXT',
  };

  // ── Column keys — translated strings used as both header names and row data keys
  private readonly COL_NAME      = this.translocoService.translate(this.i18nKeys.colName);
  private readonly COL_EMAIL     = this.translocoService.translate(this.i18nKeys.colEmail);
  private readonly COL_VIEW_USER = '';

  readonly headers: HeaderConfig[] = [
    { name: this.COL_NAME,      sortable: true,  columnWidth: '50%' },
    { name: this.COL_EMAIL,     sortable: true,  columnWidth: '40%' },
    { name: this.COL_VIEW_USER, sortable: false, columnWidth: '10%' },
  ];
  readonly initialSortColumn    = this.COL_NAME;
  readonly initialSortDirection: SortDirection = 'asc';
  readonly loadingSize = 'medium';

  readonly searchTerm = signal('');
  private readonly allUsers = signal<IInputUser[]>([]);

  readonly tableData = computed<TableRow[]>(() => {
    const locale = this.translocoService.getActiveLang() || 'en';
    const term = localeLowerCase(this.searchTerm(), locale).trim();
    const users = term
      ? this.allUsers().filter((u: IInputUser) =>
          localeLowerCase(`${u.firstName} ${u.lastName}`, locale).includes(term) ||
          localeLowerCase(u.email, locale).includes(term)
        )
      : this.allUsers();
    return users.map((u: IInputUser) => this.toTableRow(u));
  });

  get hasNoUsers(): boolean { return !this.isLoading && this.allUsers().length === 0; }
  get hasNoResults(): boolean { return !this.isLoading && this.allUsers().length > 0 && this.tableData().length === 0; }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['users']) {
      this.searchTerm.set('');
      const seen = new Set<string>();
      this.allUsers.set(
        (this.users ?? []).filter((u: IInputUser) => {
          if (seen.has(u.userUuid)) return false;
          seen.add(u.userUuid);
          return true;
        })
      );
    }
  }

  onSearchChange(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  // Sorting on this column follows the rendered string, so it orders by first name.
  private fullName(user: IInputUser): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  }

  private toTableRow(user: IInputUser): TableRow {
    return {
      id: user.userUuid,
      [this.COL_NAME]:      { type: 'text', value: this.fullName(user) },
      [this.COL_EMAIL]:     { type: 'text', value: user.email ?? '' },
      [this.COL_VIEW_USER]: { type: 'text', value: this.translocoService.translate(this.i18nKeys.colViewUser), kind: 'action' },
    } as unknown as TableRow;
  }

  onRowClick(event: { rowId: string | number; rowData: TableRow; event: Event }): void {
    const user = this.allUsers().find((u: IInputUser) => u.userUuid === event.rowId);
    this.router.navigate(['users', event.rowId], {
      relativeTo: this.route,
      state: {
        firstName: user?.firstName ?? '',
        lastName:  user?.lastName  ?? '',
        email:     user?.email     ?? '',
      },
    });
  }
}
